import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Label } from "../../@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { api_endpoint } from "@/constants/constant";
import { ClipLoader } from "react-spinners";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "not started", label: "Not started" },
  { value: "completed", label: "Completed" },
  { value: "not completed", label: "Not completed" },
];

function normalizeStatus(value) {
  const raw = String(value ?? "").trim().toLowerCase();
  const allowed = new Set(STATUS_OPTIONS.map((o) => o.value));
  return allowed.has(raw) ? raw : "active";
}

export default function UpdateCohortEnrollmentStatus({
  open,
  onOpenChange,
  userId,
  cohortId,
  courseId,
  currentStatus,
  studentName,
  cohortName,
  courseName,
  onUpdated,
  refetchEnrolments,
}) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(normalizeStatus(currentStatus));

  useEffect(() => {
    if (!open) return;
    setStatus(normalizeStatus(currentStatus));
  }, [open, currentStatus]);

  const canSubmit =
    Boolean(String(userId || "").trim()) &&
    cohortId != null &&
    courseId != null &&
    Boolean(String(status || "").trim());

  const statusLabel = useMemo(() => {
    const found = STATUS_OPTIONS.find((o) => o.value === status);
    return found?.label ?? status;
  }, [status]);

  const handleSave = async () => {
    if (!canSubmit) {
      toast.error("Missing enrollment identifiers or status.");
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const payload = {
        user_id: String(userId),
        cohort_id: Number(cohortId),
        course_id: Number(courseId),
        status: String(status),
      };

      await axios.put(`${api_endpoint}/cohort/enrollment/update`, payload, reqConfig);

      toast.success(
        `Updated status to "${statusLabel}" for ${studentName || "student"}.`
      );
      onOpenChange(false);
      onUpdated?.(payload);
      // Defer so parent refetch isn't affected by dialog state batching.
      if (refetchEnrolments) setTimeout(() => refetchEnrolments(), 0);
    } catch (error) {
      console.error("Error updating cohort enrollment status:", error);
      toast.error("Failed to update enrollment status.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md text-(--neutral-900)">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            Change Enrollment Status
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-(--neutral-300) bg-(--surface-base) p-3">
            <h6 className="p-0 m-0">
              Student
            </h6>
            <div className="font-normal text-(--neutral-900)">
              {studentName || "Not available"}
            </div>
            {(cohortName || courseName) && (
              <div className="mt-1 font-body text-sm text-(--neutral-600)">
                {cohortName ? `Cohort: ${cohortName}` : null}
                {cohortName && courseName ? " • " : null}
                {courseName ? `Course: ${courseName}` : null}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="font-body text-(--neutral-800)">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full bg-(--surface-base)">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex gap-2 justify-between">
          <DialogClose asChild>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button variant="" onClick={handleSave} disabled={loading || !canSubmit}>
            {loading ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
